import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { cn } from '@/utils'
import { useAnnotationLayer } from './queries'
import { ALL_ANNOTATIONS_ICONS, DEFAULT_ANNOTATIONS_MENUS } from './menus'
import {
  DEFAULT_STROKE_COLOR,
  DEFAULT_ANNOTATION_SIZE,
  STROKE_COLOR_VARIATIONS,
} from './constants'
import { AnnotationSizes, IconStrokes, MenuIconSizes } from './enums'
import { Line } from './Line'
import {
  calculateLineValues,
  isLine,
  isArrow,
  getAbsoluteYFromSubPage,
} from './utils'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useDocumentState } from '@/state'
import { DocumentPage } from '@/lib/api/client'
import { OptionsContextMenu } from '@/components/core/OptionsContextMenu'
import {
  TemplateAttribute,
  AnnotationTemplate,
  useUpdateTemplateElement,
  Position,
} from '@/services/hooks/template_attributes'
import { useKeyboardNudge } from '@/utils/hooks/useKeyboardNudge'
import { useMultiSelectDraggable } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useMultiSelectDraggable'
import { useRelativeToAbsolutePositioning } from '@/utils/hooks/useRelativeToAbsolutePositioning'
import { useIsTemplateAttributeMultiSelected } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useIsTemplateAttributeMultiSelected'
import { useMultiSelectHandler } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useMultiSelectHandler'

type DraggableAnnotationProps = {
  template: TemplateAttribute<AnnotationTemplate>
  documentPage: DocumentPage
  isReadOnly: boolean
  dataType: 'annotation'
  onNudgeStart?: (showGrid: boolean) => void
  onNudgeEnd?: (hideGrid: boolean) => void
}
export const DraggableAnnotation = ({
  template,
  documentPage,
  isReadOnly,
  dataType,
  onNudgeStart,
  onNudgeEnd,
}: DraggableAnnotationProps) => {
  const {
    id,
    template_values: {
      position: updatedPosition = { x: 0, y: 0 },
      size = DEFAULT_ANNOTATION_SIZE,
      strokeColor,
      type,
      startPoint,
      endPoint,
      bubbleValue,
      text: textContent,
      subPageId,
    },
  } = useMemo(() => template, [template])
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const selectedElement = useDocumentState((state) => state.selectedElement)
  const croppingImageId = useDocumentState((state) => state.croppingImageId)
  const selectElement = useDocumentState((state) => state.setSelectedElement)
  const setIsUserSelectDisabled = useDocumentState(
    (state) => state.setIsUserSelectDisabled,
  )
  const {
    onPointerDown: multiSelectKeydownHandler,
    onPointerUp: multiSelectKeyupHandler,
  } = useMultiSelectHandler()
  const [isResizing, setIsResizing] = useState(false)
  const {
    bringForward,
    bringToFront,
    sendBackward,
    sendToBack,
    isLoading: isOptionsMenuLoading,
    maxZIndex,
  } = useAnnotationLayer(documentPage.id as string)
  const { mutateAsync: updateTemplate } = useUpdateTemplateElement({
    documentPageId: documentPage?.id as string,
  })
  const zoom = useDocumentState((state) => state.zoom)
  const zoomRatio = useMemo(() => zoom / 100, [zoom])
  const INITIAL_DELTA = useMemo(() => ({ x: 0, y: 0 }), [])
  const documentSubPages = useMemo(
    () =>
      (documentPage?.template_values?.subPages as Array<string>) ?? [
        documentPage?.id,
      ],
    [documentPage],
  )

  const [{ position, delta }, setPosition] = useState({
    position: updatedPosition,
    delta: INITIAL_DELTA,
  })
  const [startPointDelta, setStartPointDelta] = useState<Position | null>(null)
  const [endPointDelta, setEndPointDelta] = useState<Position | null>(null)
  const dragInfoRef = useRef<{
    dragging: 'start' | 'end' | null
    mouseStart: { x: number; y: number }
  }>({
    dragging: null,
    mouseStart: { x: 0, y: 0 },
  })

  const updatedStartPoint = useMemo(
    () => ({
      x: (startPoint?.x || 0) + (startPointDelta?.x || 0),
      y: (startPoint?.y || 0) + (startPointDelta?.y || 0),
    }),
    [startPoint, startPointDelta],
  )

  const updatedEndPoint = useMemo(
    () => ({
      x: (endPoint?.x || 0) + (endPointDelta?.x || 0),
      y: (endPoint?.y || 0) + (endPointDelta?.y || 0),
    }),
    [endPoint, endPointDelta],
  )
  const iconData = useMemo(
    () => ALL_ANNOTATIONS_ICONS.find((icon) => type === icon.id),
    [type],
  )
  const Icon = useMemo(() => iconData?.Icon, [iconData?.Icon]) as any
  const colorOptionsEnabled = useMemo(
    () =>
      DEFAULT_ANNOTATIONS_MENUS.find((menu) =>
        menu.menuItems.some((item) => item.id === type),
      )?.colorOptionsEnabled,
    [type],
  )
  const isSelected = useMemo(
    () => selectedElement?.id === id,
    [selectedElement?.id, id],
  )
  const isMultiSelected = useIsTemplateAttributeMultiSelected({
    templateAttributeId: template.id,
  })
  const {
    attributes,
    isDragging,
    listeners,
    transform,
    setActivatorNodeRef,
    setNodeRef,
  } = useMultiSelectDraggable({
    id: id as string,
    disabled: isAddingElement || isReadOnly || !!croppingImageId,
    data: {
      type: dataType,
    },
  })

  const { bind, previewDelta } = useKeyboardNudge({
    isActive: isSelected && Boolean(selectedElement?.isRemovable),
    onNudgeStart,
    onNudgeEnd,
    template,
    documentPage,
  })

  useEffect(() => {
    const tX = transform?.x ?? 0
    const tY = transform?.y ?? 0
    const pX = previewDelta?.x ?? 0
    const pY = previewDelta?.y ?? 0

    // If everything is 0, this is typically the "drag ended" / "idle" state.
    // Don't overwrite delta here — let it be reset only when positionData changes.
    if (tX === 0 && tY === 0 && pX === 0 && pY === 0) {
      return
    }

    setPosition((prevPosition) => ({
      ...prevPosition,
      delta: {
        x: (tX + pX) / zoomRatio,
        y: (tY + pY) / zoomRatio,
      },
    }))
  }, [transform, previewDelta, zoomRatio])

  useEffect(() => {
    setPosition({
      position: updatedPosition,
      delta: INITIAL_DELTA,
    })
  }, [INITIAL_DELTA, updatedPosition])

  const snapVectorToAngles = useCallback((vx: number, vy: number) => {
    const mag = Math.hypot(vx, vy)
    if (mag === 0) return { x: 0, y: 0 }
    const theta = Math.atan2(vy, vx)
    let deg = Math.abs(theta * (180 / Math.PI)) % 180
    if (deg > 90) deg = 180 - deg
    const snaps = [0, 15, 30, 45, 60, 75, 90]
    let nearest = snaps[0]
    let minDiff = Infinity
    for (const s of snaps) {
      const d = Math.abs(deg - s)
      if (d < minDiff) {
        minDiff = d
        nearest = s
      }
    }
    const snappedRad = (nearest * Math.PI) / 180
    const ux = Math.cos(snappedRad)
    const uy = Math.sin(snappedRad)
    const sx = vx === 0 ? Math.sign(Math.cos(theta)) || 1 : Math.sign(vx)
    const sy = vy === 0 ? Math.sign(Math.sin(theta)) || 1 : Math.sign(vy)
    return { x: ux * mag * sx, y: uy * mag * sy }
  }, [])

  const beginDrag = useCallback(
    (which: 'start' | 'end', e: React.MouseEvent) => {
      e.stopPropagation()
      setIsUserSelectDisabled(true)
      setIsResizing(true)
      dragInfoRef.current = {
        dragging: which,
        mouseStart: { x: e.clientX, y: e.clientY },
      }
    },
    [dragInfoRef],
  )

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragInfoRef.current.dragging) return
      const { dragging, mouseStart } = dragInfoRef.current
      const dx = (e.clientX - mouseStart.x) / zoomRatio
      const dy = (e.clientY - mouseStart.y) / zoomRatio

      const baseStartAbs = {
        x: (startPoint?.x || 0) + (startPointDelta?.x || 0),
        y: (startPoint?.y || 0) + (startPointDelta?.y || 0),
      }
      const baseEndAbs = {
        x: (endPoint?.x || 0) + (endPointDelta?.x || 0),
        y: (endPoint?.y || 0) + (endPointDelta?.y || 0),
      }

      if (dragging === 'start') {
        const candidateAbs = {
          x: (startPoint?.x || 0) + dx,
          y: (startPoint?.y || 0) + dy,
        }
        const fixedAbs = baseEndAbs
        let vx = candidateAbs.x - fixedAbs.x
        let vy = candidateAbs.y - fixedAbs.y
        if (e.shiftKey) {
          const snapped = snapVectorToAngles(vx, vy)
          vx = snapped.x
          vy = snapped.y
        }
        const targetAbs = { x: fixedAbs.x + vx, y: fixedAbs.y + vy }
        setStartPointDelta({
          x: targetAbs.x - (startPoint?.x || 0),
          y: targetAbs.y - (startPoint?.y || 0),
        })
      } else if (dragging === 'end') {
        const candidateAbs = {
          x: (endPoint?.x || 0) + dx,
          y: (endPoint?.y || 0) + dy,
        }
        const fixedAbs = baseStartAbs
        let vx = candidateAbs.x - fixedAbs.x
        let vy = candidateAbs.y - fixedAbs.y
        if (e.shiftKey) {
          const snapped = snapVectorToAngles(vx, vy)
          vx = snapped.x
          vy = snapped.y
        }
        const targetAbs = { x: fixedAbs.x + vx, y: fixedAbs.y + vy }
        setEndPointDelta({
          x: targetAbs.x - (endPoint?.x || 0),
          y: targetAbs.y - (endPoint?.y || 0),
        })
      }
    }
    const onMouseUp = async () => {
      setIsUserSelectDisabled(false)
      setIsResizing(false)

      if (dragInfoRef.current.dragging) {
        dragInfoRef.current.dragging = null
        setTimeout(() => {
          selectElement({
            documentPageId: documentPage.id as string,
            id: id as string,
            isRemovable: true,
            type: dataType,
          })
        })

        setStartPointDelta(null)
        setEndPointDelta(null)

        await updateTemplate({
          data_type: dataType,
          templateAttributeId: id as string,
          payload: {
            startPoint: updatedStartPoint,
            endPoint: updatedEndPoint,
            position: {
              ...template.template_values.position,
              x: position.x + (startPointDelta?.x || 0),
              y: position.y + (startPointDelta?.y || 0),
            },
          },
        })
      }
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [
    template.template_values.position,
    updatedEndPoint,
    updatedStartPoint,
    snapVectorToAngles,
    zoomRatio,
    startPoint,
    endPoint,
    startPointDelta,
    endPointDelta,
  ])

  const wrapperStyles = useMemo(() => {
    const styles = {
      translate: `${delta.x}px ${delta.y}px`,
      top: getAbsoluteYFromSubPage({
        subPageId: subPageId ?? (documentPage?.id as string),
        relativeY: position.y + (startPointDelta?.y || 0),
        subPageIds: documentSubPages,
      }),
      left: position.x + (startPointDelta?.x || 0),
      zIndex: updatedPosition?.z || 1000,
      borderColor: isSelected || isMultiSelected ? '#D2E8FF' : 'transparent',
    } as React.CSSProperties

    if (isLine(type)) {
      const { inclination } = calculateLineValues({
        startPoint: updatedStartPoint as Position,
        endPoint: updatedEndPoint as Position,
      })
      styles.transformOrigin = '2px center'
      styles.rotate = `${inclination}deg`
    }

    return styles
  }, [
    delta,
    position,
    isSelected,
    isMultiSelected,
    documentSubPages,
    subPageId,
    documentPage?.id,
    updatedEndPoint,
    updatedStartPoint,
    type,
    updatedPosition?.z,
  ])

  const strokeColorOptions = useMemo(
    () =>
      [...STROKE_COLOR_VARIATIONS, DEFAULT_STROKE_COLOR].filter(
        (color) => color !== (strokeColor ?? DEFAULT_STROKE_COLOR),
      ),
    [strokeColor],
  )

  const withText = useMemo(() => isArrow(type) && type.includes('text'), [type])
  const textPosition = useMemo(
    () => (withText && type.split('--')[1]) || '',
    [type, withText],
  )

  const [text, setText] = useState(textContent || '')

  useEffect(() => {
    setText(textContent || '')
  }, [textContent])

  const onTextBlur = useCallback(() => {
    void updateTemplate({
      data_type: dataType,
      templateAttributeId: id as string,
      payload: {
        text,
      },
    })
  }, [updateTemplate, id, text, dataType])

  const { setDraggableAnnotationParentRef } = useRelativeToAbsolutePositioning()

  const optionsGroups = useMemo(
    () => [
      {
        options: [
          {
            label: 'Bring to front',
            action: () => bringToFront(id as string),
          },
          {
            label: 'Bring forward',
            action: () => bringForward(id as string),
          },
          {
            label: 'Send backward',
            action: () => sendBackward(id as string),
          },
          { label: 'Send to back', action: () => sendToBack(id as string) },
        ],
      },
    ],
    [bringForward, bringToFront, sendBackward, sendToBack, id],
  )

  return (
    <OptionsContextMenu
      isReadonly={isReadOnly}
      isDisabled={isOptionsMenuLoading}
      onAuxClick={() => {
        selectElement({
          documentPageId: documentPage.id as string,
          id: id as string,
          isRemovable: true,
          type: dataType,
        })
      }}
      optionsGroups={optionsGroups}
      zIndex={maxZIndex + 3}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                'absolute box-content border-2 rounded-md outline-transparent transition-transform',
                !isLine(type) &&
                  size &&
                  (AnnotationSizes[size] ?? `w-${size} h-${size}`),
                {
                  'border-dashed': isSelected || isMultiSelected,
                  'shadow-md': isDragging,
                  'shadow-gray-200': isDragging,
                  'cursor-grab': !isDragging && !isAddingElement && !isReadOnly,
                  'cursor-default': isReadOnly,
                  'cursor-grabbing': isDragging,
                  'bg-sky-200/10': isDragging,
                  'scale-100': !isDragging,
                  'scale-105': isDragging && !isLine(type),
                  'z-[100]': isSelected,
                },
              )}
              data-testid={`annotation-${type}-${size}`}
              style={wrapperStyles}
              ref={setNodeRef}
              {...bind}
              {...attributes}
              onPointerDownCapture={(e) => {
                multiSelectKeydownHandler(e as any, template)
              }}
              onPointerUpCapture={(e) => {
                multiSelectKeyupHandler(e)
              }}
            >
              <div
                className="w-full h-full relative"
                ref={(el) => {
                  setActivatorNodeRef(el)
                  if (template.id) {
                    setDraggableAnnotationParentRef(template.id, el)
                  }
                }}
                {...listeners}
              >
                {!isLine(type) ? (
                  Icon ? (
                    <Icon
                      strokeWidth={IconStrokes[size || 'small']}
                      className="h-full w-full"
                      strokeColor={strokeColor || DEFAULT_STROKE_COLOR}
                    />
                  ) : null
                ) : (
                  <>
                    {isSelected && (
                      <>
                        <div
                          className="resize-handle resize-handle--left !cursor-ew-resize"
                          onMouseDown={(e) => beginDrag('start', e)}
                          style={{ transform: 'translateX(-120%)' }}
                        />
                        <div
                          className="resize-handle resize-handle--right !cursor-ew-resize"
                          onMouseDown={(e) => beginDrag('end', e)}
                          style={{ transform: 'translateX(120%)' }}
                        />
                      </>
                    )}
                    <Line
                      subPageId={subPageId}
                      subPages={documentPage.template_values.subPages}
                      startPoint={updatedStartPoint as Position}
                      endPoint={updatedEndPoint as Position}
                      isArrow={isArrow(type)}
                      isBubble={type.includes('bubble')}
                      withText={withText}
                      textPosition={textPosition}
                      text={text}
                      updateText={(e) => setText(e.target.value)}
                      onTextBlur={onTextBlur}
                      bubbleValue={bubbleValue}
                      strokeColor={strokeColor || DEFAULT_STROKE_COLOR}
                      strokeWidth={IconStrokes[size || 'small']}
                    />
                  </>
                )}
              </div>
              {colorOptionsEnabled &&
                isSelected &&
                !isDragging &&
                !isResizing && (
                  <div className="relative">
                    <div className="absolute flex p-1 justify-center left-1/2 -translate-x-1/2 bg-slate-100/80 shadow-md w-[155px] flex flex-wrap top-2">
                      {strokeColorOptions.map((color) => (
                        <Button
                          className="mx-0.5 flex items-center justify-center w-8 h-8 p-0.5"
                          key={`annotation-stroke-color--${color}`}
                          variant="outline"
                          onClick={(e) => {
                            void updateTemplate({
                              data_type: dataType,
                              templateAttributeId: id as string,
                              payload: {
                                strokeColor:
                                  color === DEFAULT_STROKE_COLOR ? null : color,
                              },
                            })
                            e.stopPropagation()
                          }}
                        >
                          <Icon
                            className={
                              MenuIconSizes[size || ''] ??
                              MenuIconSizes['medium']
                            }
                            strokeWidth={(IconStrokes[size || ''] ?? 0) + 1}
                            strokeColor={color}
                          />
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </TooltipTrigger>
          {iconData?.description && !isDragging && (
            <TooltipContent className="text-white bg-black text-xs">
              <p>{iconData.description}</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </OptionsContextMenu>
  )
}
